/**
 * GalleryGenieCard — the image-forward card for the immersive /genies gallery.
 *
 * Mirrors the LIVE CompactGenieCard's data mapping (real brand logo, brand
 * colour, mascot, business line) but rebuilds the LAYOUT to the wireframe: a big
 * brand-tinted disc as the hero showing the genie's REAL logo (falling back to
 * its mascot), the genie name, the business it answers for, a quiet Live / In
 * setup dot, "View all details" -> /genies/:id (viewer) and a quiet secondary
 * "Configure" -> /genies/:id/immersive-setup (configurator).
 *
 * Brand law: genie tokens only; the disc tint uses the genie's own primaryColor
 * when set, else cycles the brand palette; the Live dot uses genie-success
 * (wireframe green -> genie-success). No raw hex outside that palette.
 */
import { useNavigate } from "react-router-dom";
import { Settings } from "lucide-react";
import { VoiceMascot } from "@/components/voice-genie/VoiceMascot";
import { mascotForAgent } from "@/components/voice-genie/GenieOwnerDetail";
import { resolveGenieAvatarUrl } from "@/utils/image/genieAvatars";
import { GenieCategories } from "@/types/shared/categories";
import type { Agent } from "@/types/agent/agent";

/** Brand palette tints the disc glow cycles through (genie-* only). */
const DISC_TINTS = ["#4E9CFF", "#FF4DB8", "#FFBB33"] as const;

const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

/** Hex + alpha for the soft radial glow (alpha 0..1). */
const tintAlpha = (hex: string, a: number) =>
  hex + Math.round(a * 255).toString(16).padStart(2, "0");

/** Plain-language business line: category label, else description, else a calm default. */
const businessLine = (genie: Agent): string => {
  const cat =
    genie.category_data?.label ||
    GenieCategories.find((c) => c.id === genie.category)?.label;
  if (cat && cat !== "Other") return cat;
  if (genie.description) return genie.description;
  return "Your business";
};

interface GalleryGenieCardProps {
  genie: Agent;
  /** Index in the grid — picks a deterministic disc tint from the brand palette. */
  index: number;
}

export const GalleryGenieCard = ({ genie, index }: GalleryGenieCardProps) => {
  const navigate = useNavigate();
  // Real brand colour when the genie has one, else cycle the brand palette.
  const tint =
    genie.branding?.primaryColor || DISC_TINTS[index % DISC_TINTS.length];
  const mascot = mascotForAgent(genie);
  // Real brand logo (may be undefined — full fallback chain inside the resolver).
  const brandLogoUrl = resolveGenieAvatarUrl(genie);
  const isLive = genie.is_active;
  const goToDetails = () => navigate(`/genies/${genie.id}`);

  return (
    <div
      role="button"
      tabIndex={0}
      data-hg-block={`card-${slug(genie.name || "genie")}`}
      onClick={goToDetails}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          goToDetails();
        }
      }}
      className="group flex cursor-pointer flex-col items-center rounded-[22px] border border-genie-border bg-white px-[22px] pt-[30px] pb-[26px] text-center shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl"
    >
      {/* big brand-tinted disc — the hero of the card (real logo, else mascot) */}
      <div
        className={`flex items-center justify-center rounded-full overflow-hidden ${
          isLive ? "" : "grayscale opacity-70"
        }`}
        style={{
          width: 156,
          height: 156,
          background: `radial-gradient(circle at 50% 38%, ${tintAlpha(tint, 0.18)} 0%, ${tintAlpha(tint, 0.06)} 70%)`,
          boxShadow: `0 0 34px ${tintAlpha(tint, 0.22)}`,
        }}
      >
        {brandLogoUrl ? (
          <img
            src={brandLogoUrl}
            alt=""
            style={{
              maxWidth: "90%",
              maxHeight: "90%",
              objectFit: "contain",
              display: "block",
            }}
          />
        ) : (
          <VoiceMascot mascot={mascot} size={138} />
        )}
      </div>

      <h3 className="mt-[18px] text-[19px] font-extrabold tracking-[-0.3px] text-[#222B45]">
        {genie.name || "Untitled genie"}
      </h3>
      <p className="mt-[3px] text-[13px] text-[#94A3B8] line-clamp-1 max-w-full">
        {businessLine(genie)}
      </p>

      {/* quiet status dot — Live -> genie-success */}
      <div className="mt-[10px] flex items-center gap-1.5">
        <span
          className={`w-[7px] h-[7px] rounded-full ${
            isLive ? "bg-genie-success" : "bg-gray-300"
          }`}
        />
        <span
          className={`text-[11.5px] font-semibold ${
            isLive ? "text-genie-success" : "text-gray-400"
          }`}
        >
          {isLive ? "Live" : "In setup"}
        </span>
      </div>

      {/* primary "View all details" + a quiet secondary "Configure" door */}
      <div className="mt-4 flex items-center gap-3">
        <span
          className="text-[13.5px] font-bold transition-opacity group-hover:opacity-80"
          style={{ color: tint }}
        >
          View all details
        </span>
        <span
          role="button"
          tabIndex={0}
          aria-label="Configure genie"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/genies/${genie.id}/immersive-setup`);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              navigate(`/genies/${genie.id}/immersive-setup`);
            }
          }}
          className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[12px] font-semibold text-gray-400 transition-colors hover:text-genie-primary hover:bg-genie-primary/5"
        >
          <Settings className="h-3.5 w-3.5" />
          Configure
        </span>
      </div>
    </div>
  );
};

export default GalleryGenieCard;
